import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { company_admin: 'Admins', dispatcher: 'Dispatchers', driver: 'Drivers' };

const emptyForm = {
  title: '', body: '', priority: 'info',
  audience_roles: ['company_admin', 'dispatcher', 'driver'],
  expires_at: '',
  scheduled_for: '',
  recurrence: 'none',
};

const AnnouncementComposeModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveAsName, setSaveAsName] = useState('');

  const toggleRole = (r) => setForm((f) => ({
    ...f,
    audience_roles: f.audience_roles.includes(r) ? f.audience_roles.filter((x) => x !== r) : [...f.audience_roles, r],
  }));

  const loadTemplates = () => axios.get(`${API}/announcement-templates`, { withCredentials: true })
    .then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (tid) => {
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({
      ...f,
      title: t.title || '',
      body: t.body || '',
      priority: t.priority || 'info',
      audience_roles: Array.isArray(t.audience_roles) && t.audience_roles.length ? t.audience_roles : f.audience_roles,
      recurrence: t.recurrence || 'none',
    }));
    toast.success(`Loaded template "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    if (!saveAsName.trim()) return toast.error('Give the template a name first');
    if (!form.title || !form.body) return toast.error('Fill title + body before saving');
    try {
      await axios.post(`${API}/announcement-templates`, {
        name: saveAsName.trim(),
        title: form.title,
        body: form.body,
        priority: form.priority,
        audience_roles: form.audience_roles,
        recurrence: form.recurrence || 'none',
      }, { withCredentials: true });
      toast.success('Template saved');
      setSaveAsName('');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const submit = async (e, rehearsal = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (form.audience_roles.length === 0) {
      toast.error('Pick at least one audience role');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        priority: form.priority,
        audience_roles: form.audience_roles,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
        recurrence: form.recurrence || 'none',
        rehearsal,
      };
      const res = await axios.post(`${API}/announcements`, payload, { withCredentials: true });
      if (rehearsal) {
        toast.success('Test sent to your inbox — check your bell notifications');
      } else {
        toast.success(res.data?.status === 'scheduled' ? 'Announcement scheduled' : 'Announcement broadcast');
      }
      onSaved();
      if (!rehearsal) onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error('Your session expired — please log in again.');
      } else {
        toast.error(err.response?.data?.detail || 'Broadcast failed');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="announcement-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            New announcement
          </h3>
          <button onClick={onClose} data-testid="announcement-modal-close" className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {templates.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="template-picker">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Load from template</label>
              <select
                onChange={(e) => applyTemplate(e.target.value)}
                defaultValue=""
                data-testid="template-select"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              >
                <option value="">— Start from a saved template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.priority})</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Title</label>
            <input
              required maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="announcement-title"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Message</label>
            <textarea
              required rows={5} maxLength={5000}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="announcement-body"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="announcement-priority"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="urgent">Urgent (emails recipients)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Expires (optional)</label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="announcement-expires"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Schedule (optional)</label>
              <input
                type="datetime-local"
                value={form.scheduled_for}
                onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                data-testid="announcement-scheduled"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">Leave empty to send immediately.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Recurrence</label>
              <select
                value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                data-testid="announcement-recurrence"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              >
                <option value="none">One-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Audience</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(ROLE_LABEL).map(([r, label]) => {
                const on = form.audience_roles.includes(r);
                return (
                  <button
                    key={r} type="button" onClick={() => toggleRole(r)}
                    data-testid={`announcement-role-${r}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${on ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-1">Recipients get an in-app bell notification. Urgent priority also sends email.</p>
          </div>
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200" data-testid="save-as-template">
            <input
              type="text"
              placeholder="Save current as template (name)"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              data-testid="save-as-template-name"
              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
            <button type="button" onClick={saveAsTemplate}
              data-testid="save-as-template-btn"
              className="text-xs font-semibold text-[#002FA7] hover:text-[#00247D] whitespace-nowrap">
              Save as template
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
            <button
              type="button" onClick={() => submit(null, true)} disabled={saving}
              data-testid="announcement-rehearsal"
              title="Send this only to yourself as a preview — nobody else will receive it."
              className="px-4 py-2 border border-[#002FA7] text-[#002FA7] text-sm font-semibold rounded-md hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
            >
              Send test to me
            </button>
            <button
              type="submit" disabled={saving}
              data-testid="announcement-save"
              className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Broadcast
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementComposeModal;
